import { useForm } from 'react-hook-form';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ActionButton } from '../../components/Forms/ActionButton';
import { useCreateProject, useGetProjectById, useUpdateProject } from '../../queries/project';
import { useGetActiveOrganiser } from '../../queries/user';
import ActiveStatus from '../../components/Common/ActiveStatus';
import { useEffect, useState } from 'react';
import NewDropdown from '../../components/Forms/NewDropdown';
import { useLocation, useNavigate } from 'react-router-dom';
import MediaUpload from '../../components/Forms/MediaUpload';
import MultiMediaUploadSection from '../../components/Forms/MultiMediaUploadSection';

const CreateProject = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const projectId = state?.projectId || null;

    const { data: projectData, isLoading: isProjectDataLoading } = useGetProjectById(projectId);

    // Fetch data for select options
    const { data: organiserList = [] } = useGetActiveOrganiser();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        watch,
        setValue,
        trigger,
    } = useForm({
        defaultValues: {
            projectName: '',
            projectType: '',
            eventCategory: '',
            clientId: '',
            location: '',
            address: '',
            shortDescription: '',
            longDescription: '',
            projectDeveloperMessage: '',
            projectSponsorMessage: '',
            volunteerInstructions: '',
            eventStartDateTime: '',
            eventEndDateTime: '',
            notes: '',
        }
    });

    const [active, setActive] = useState(true);
    const [displayImage, setDisplayImage] = useState(null);
    const [projectPhotos, setProjectPhotos] = useState([]);
    const [mediaClear, setMediaClear] = useState(false);

    // Project Type options
    const projectTypeOptions = [
        { _id: 'Plantation', name: 'Plantation' },
        { _id: 'Volunteering Event', name: 'Volunteering Event' },
        { _id: 'Carbon Offset', name: 'Carbon Offset' },
    ];

    // Event Category options
    const eventCategoryOptions = [
        { _id: 'Environment', name: 'Environment' },
        { _id: 'Community', name: 'Community' },
        { _id: 'Education', name: 'Education' },
        { _id: 'Health', name: 'Health' },
        { _id: 'Wildlife', name: 'Wildlife' },
        { _id: 'Clean Energy', name: 'Clean Energy' },
        { _id: 'Waste Management', name: 'Waste Management' },
    ];

    const { mutateAsync: createProject, isPending: isCreating } = useCreateProject();
    const { mutateAsync: updateProject, isPending: isUpdating } = useUpdateProject(projectId);

    const watchedProjectType = watch("projectType");
    const watchedEventCategory = watch("eventCategory");
    const watchedClientId = watch("clientId");

    const onSubmit = (data) => {
        // Validate required fields
        if (!data.projectName) {
            toast.error('Please enter project name');
            return;
        }

        if (!data.clientId) {
            toast.error('Please select a client');
            return;
        }

        if (!data.location) {
            toast.error('Please enter location');
            return;
        }

        if (!data.eventStartDateTime || !data.eventEndDateTime) {
            toast.error('Please select event start and end date and time');
            return;
        }

        if (!displayImage) {
            toast.error('Please upload a display image');
            return;
        }

        // Find the selected organizer to determine if it's Company or NGO
        const selectedOrganizer = organiserList.find(org => org._id === data.clientId);

        const finalData = {
            active: active,
            name: data.projectName,
            type: data.projectType,
            eventCategory: data.eventCategory,
            location: data.location,
            address: data.address || '',
            shortDescription: data.shortDescription || '',
            longDescription: data.longDescription || '',
            projectDeveloperMessage: data.projectDeveloperMessage || '',
            projectSponsorMessage: data.projectSponsorMessage || '',
            instructionsForVolunteers: data.volunteerInstructions || '',
            eventStartDateTime: data.eventStartDateTime,
            eventEndDateTime: data.eventEndDateTime,
            imageUrl: displayImage || '',
            projectImages: projectPhotos || [],
            notes: data.notes || '',
        }

        // Set either companyId or ngoId based on organizer type
        if (selectedOrganizer?.organiserType === 'Company') {
            finalData.companyId = data.clientId;
            finalData.ngoId = null;
        } else if (selectedOrganizer?.organiserType === 'NGO') {
            finalData.ngoId = data.clientId;
            finalData.companyId = null;
        }

        console.log('Processed project data:', finalData);

        if (projectId) {
            finalData.id = projectId;
            updateProject(finalData, {
                onSuccess: (res) => {
                    if (res.error) {
                        console.log(res);
                        toast.error(res.error);
                    } else {
                        toast.success('Project updated successfully!');
                        setTimeout(() => {
                            navigate('/projects/list-projects');
                        }, 2000);
                    }
                },
                onError: (error) => {
                    console.error('Error updating project:', error);
                    toast.error('Failed to update project. Please try again.');
                }
            });
        } else {
            createProject(finalData, {
                onSuccess: (res) => {
                    if (res.error) {
                        console.log(res);
                        toast.error(res.error);
                    } else {
                        toast.success('Project created successfully!');
                        reset();
                        setDisplayImage(null);
                        setProjectPhotos([]);
                        setTimeout(() => {
                            navigate('/projects/list-projects');
                        }, 2000);
                    }
                },
                onError: (error) => {
                    console.error('Error creating project:', error);
                    toast.error('Failed to create project. Please try again.');
                }
            });
        }
    };

    useEffect(() => {
        setMediaClear(false);
    }, [displayImage]);

    useEffect(() => {
        setTimeout(() => {
            if (projectData) {
                setActive(projectData.active !== undefined ? projectData.active : true);
                setValue("projectName", projectData.name || '');
                setValue("projectType", projectData.type || '');
                setValue("eventCategory", projectData.eventCategory || '');

                // Handle both companyId and ngoId - one will be populated, the other will be null
                let clientId = '';
                if (projectData.companyId) {
                    clientId = typeof projectData.companyId === 'object' && projectData.companyId?._id
                        ? projectData.companyId._id
                        : projectData.companyId;
                } else if (projectData.ngoId) {
                    clientId = typeof projectData.ngoId === 'object' && projectData.ngoId?._id
                        ? projectData.ngoId._id
                        : projectData.ngoId;
                }
                setValue("clientId", clientId);

                setValue("location", projectData.location || '');
                setValue("address", projectData.address || '');
                setValue("shortDescription", projectData.shortDescription || '');
                setValue("longDescription", projectData.longDescription || '');
                setValue("projectDeveloperMessage", projectData.projectDeveloperMessage || '');
                setValue("projectSponsorMessage", projectData.projectSponsorMessage || '');
                setValue("volunteerInstructions", projectData.instructionsForVolunteers || '');
                setValue("notes", projectData.notes || '');
                // Convert ISO string to datetime-local format (YYYY-MM-DDTHH:MM)
                if (projectData.eventStartDateTime) {
                    const startDate = new Date(projectData.eventStartDateTime);
                    const localStartDateTime = new Date(startDate.getTime() - (startDate.getTimezoneOffset() * 60000))
                        .toISOString()
                        .slice(0, 16);
                    setValue("eventStartDateTime", localStartDateTime);
                }
                if (projectData.eventEndDateTime) {
                    const endDate = new Date(projectData.eventEndDateTime);
                    const localEndDateTime = new Date(endDate.getTime() - (endDate.getTimezoneOffset() * 60000))
                        .toISOString()
                        .slice(0, 16);
                    setValue("eventEndDateTime", localEndDateTime);
                }
                setDisplayImage(projectData.imageUrl);
                setProjectPhotos(projectData.projectImages || []);
            }
        }, 200);
    }, [projectData, setValue]);

    if (isProjectDataLoading || !organiserList) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-black">Loading...</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen w-full font-semibold text-base overflow-visible">
            <div className='flex items-center justify-end'>
                <ActiveStatus
                    active={active}
                    setActive={setActive}
                    isCreateForm={true}
                />
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full font-semibold overflow-visible">
                <section className="flex flex-col justify-center px-5 pt-4 py-9 gap-0 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] overflow-visible">

                    <div className="flex gap-6">
                        {/* Main Form Fields */}
                        <div className="flex-1">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                                {/* Project Name */}
                                <div className="flex flex-col shrink-0 basis-0 w-full">
                                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                        <span className="text-red text-xs inline">*</span> Project Name
                                    </label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Project Name"
                                            {...register("projectName", {
                                                required: "Project Name is required",
                                                minLength: { value: 2, message: "Project name must be at least 2 characters" }
                                            })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.projectName ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.projectName && <p className="text-red text-sm">{errors.projectName.message}</p>}
                                </div>

                                {/* Client */}
                                <div className='flex flex-col gap-1 basis-1/2'>
                                    <label htmlFor="clientId" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                                        <span className="text-red text-xs inline">* </span>
                                        Client
                                    </label>
                                    <NewDropdown
                                        name={"clientId"}
                                        {...register("clientId", {
                                            required: "Client is required"
                                        })}
                                        dataList={organiserList}
                                        value={watchedClientId}
                                        placeholder={'Select Client (Optional)'}
                                        onChange={(e) => {
                                            setValue("clientId", e.value);
                                            if (e.value) {
                                                trigger("clientId");
                                            }
                                        }}
                                    />
                                    {errors.clientId && <p className="text-red text-sm mt-1">{errors.clientId.message}</p>}
                                </div>

                                {/* Project Type */}
                                {/* <div className='flex flex-col gap-1 basis-1/2'>
                                    <label htmlFor="projectType" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                                        <span className="text-red text-xs inline">* </span>
                                        Project Type
                                    </label>
                                    <NewDropdown
                                        name={"projectType"}
                                        dataList={projectTypeOptions}
                                        value={watchedProjectType}
                                        placeholder={'Select Project Type'}
                                        onChange={(e) => {
                                            setValue("projectType", e.value);
                                            if (e.value) {
                                                trigger("projectType");
                                            }
                                        }}
                                    />
                                    {errors.projectType && <p className="text-red text-sm mt-1">{errors.projectType.message}</p>}
                                </div> */}

                                {/* Event Category */}
                                {/* <div className='flex flex-col gap-1 basis-1/2'>
                                    <label htmlFor="eventCategory" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                                        <span className="text-red text-xs inline">* </span>
                                        Event Category
                                    </label>
                                    <NewDropdown
                                        name={"eventCategory"}
                                        dataList={eventCategoryOptions}
                                        value={watchedEventCategory}
                                        placeholder={'Select Event Category'}
                                        onChange={(e) => {
                                            setValue("eventCategory", e.value);
                                            if (e.value) {
                                                trigger("eventCategory");
                                            }
                                        }}
                                    />
                                    {errors.eventCategory && <p className="text-red text-sm mt-1">{errors.eventCategory.message}</p>}
                                </div> */}

                                {/* Event Start Date Time */}
                                <div className="flex flex-col shrink-0 basis-0 w-full">
                                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                        <span className="text-red text-xs inline">*</span> Project Start Date & Time
                                    </label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="datetime-local"
                                            {...register("eventStartDateTime", {
                                                required: "Project Start Date & Time is required"
                                            })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.eventStartDateTime ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.eventStartDateTime && <p className="text-red text-sm">{errors.eventStartDateTime.message}</p>}
                                </div>
                                {/* Event End Date Time */}
                                <div className="flex flex-col shrink-0 basis-0 w-full">
                                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                        <span className="text-red text-xs inline">*</span> Project End Date & Time
                                    </label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="datetime-local"
                                            {...register("eventEndDateTime", {
                                                required: "Project End Date & Time is required"
                                            })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.eventEndDateTime ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.eventEndDateTime && <p className="text-red text-sm">{errors.eventEndDateTime.message}</p>}
                                </div>

                                {/* Location */}
                                <div className="flex flex-col shrink-0 basis-0 w-full col-span-2">
                                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                        <span className="text-red text-xs inline">*</span> Project Location
                                    </label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Location"
                                            {...register("location", {
                                                required: "Location is required",
                                                minLength: { value: 2, message: "Location must be at least 2 characters" }
                                            })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.location ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.location && <p className="text-red text-sm">{errors.location.message}</p>}
                                </div>



                                {/* Address */}
                                <div className="flex flex-col shrink-0 basis-0 w-full md:col-span-2">
                                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                        Address
                                    </label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <textarea
                                            placeholder="Enter Full Address"
                                            {...register("address")}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[80px] resize-y ${errors.address ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.address && <p className="text-red text-sm">{errors.address.message}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Image Upload Section */}
                        <div className="flex-shrink-0 flex flex-col gap-4">
                            <MediaUpload
                                key={displayImage || "new"}
                                clearMedia={mediaClear}
                                label={"* Display Image"}
                                existingImage={displayImage}
                                setImageUpload={setDisplayImage}
                                stateImage={displayImage}
                                setStateImageUpload={setDisplayImage}
                            />

                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-3">
                        {/* Volunteer Instructions - Full Width */}
                        {/* <div className="flex flex-col shrink-0 basis-0 w-full mt-6 col-span-2">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                Instructions for Volunteers
                            </label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <textarea
                                    placeholder="Enter Instructions for Volunteers"
                                    {...register("volunteerInstructions")}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[100px] resize-y ${errors.volunteerInstructions ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors.volunteerInstructions && <p className="text-red text-sm">{errors.volunteerInstructions.message}</p>}
                        </div> */}
                        {/* Short Description */}
                        <div className="flex flex-col shrink-0 basis-0 w-full">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                Description (Short)
                            </label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <textarea
                                    placeholder="Enter Short Description"
                                    {...register("shortDescription")}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[100px] resize-y ${errors.shortDescription ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors.shortDescription && <p className="text-red text-sm">{errors.shortDescription.message}</p>}
                        </div>

                        {/* Long Description */}
                        <div className="flex flex-col shrink-0 basis-0 w-full">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                Description (Long)
                            </label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <textarea
                                    placeholder="Enter Long Description"
                                    {...register("longDescription")}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[100px] resize-y ${errors.longDescription ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors.longDescription && <p className="text-red text-sm">{errors.longDescription.message}</p>}
                        </div>

                        {/* Project Developer Message */}
                        <div className="flex flex-col shrink-0 basis-0 w-full">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                Project Developer Message
                            </label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <textarea
                                    placeholder="Enter Project Developer Message"
                                    {...register("projectDeveloperMessage")}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[100px] resize-y ${errors.projectDeveloperMessage ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors.projectDeveloperMessage && <p className="text-red text-sm">{errors.projectDeveloperMessage.message}</p>}
                        </div>

                        {/* Project Sponsor Message */}
                        <div className="flex flex-col shrink-0 basis-0 w-full">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                Project Sponsor Message
                            </label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <textarea
                                    placeholder="Enter Project Sponsor Message"
                                    {...register("projectSponsorMessage")}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[100px] resize-y ${errors.projectSponsorMessage ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors.projectSponsorMessage && <p className="text-red text-sm">{errors.projectSponsorMessage.message}</p>}
                        </div>
                    </div>

                    {/* Project Photos Section */}
                    <div className="mt-6">
                        <MultiMediaUploadSection
                            title="Project Photos"
                            files={projectPhotos}
                            setFiles={setProjectPhotos}
                        />
                    </div>

                    {/* Notes Section */}
                    <div className="mt-6">
                        <div className="flex flex-col shrink-0 basis-0 w-full">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                Notes
                            </label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <textarea
                                    placeholder="Enter Notes"
                                    {...register("notes")}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[100px] resize-y ${errors.notes ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors.notes && <p className="text-red text-sm">{errors.notes.message}</p>}
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="mt-10 flex gap-5 items-center justify-between">
                        <ActionButton
                            type="button"
                            variant="reset"
                            onClick={() => {
                                reset();
                                setDisplayImage(null);
                                setProjectPhotos([]);
                                setMediaClear(true);
                                setActive(true);
                            }}
                            text="Reset"
                            disabled={isSubmitting}
                        />
                        <div className="flex items-center gap-3">
                            <ActionButton
                                variant="cancel"
                                text="Cancel"
                                handleSubmit={() => navigate('/projects/list-projects')}
                            />
                            <ActionButton
                                type="submit"
                                variant="primary"
                                text={projectId ? 'Update Project' : 'Create Project'}
                                disabled={isCreating || isUpdating}
                            />
                        </div>
                    </div>
                </section>
            </form>
            <ToastContainer
                position="top-right"
            />
        </div>
    );
}

export default CreateProject;